import { useMemo } from 'react'
import type { Task } from '@/src/renderer/lib/db/models'

export type DetailsSectionId =
  | 'title' | 'notes' | 'subtasks'
  | 'attention' | 'tags' | 'reward' | 'reminders'
  | 'start' | 'deadline' | 'duration' | 'recurrence'

/** Die vier Bereiche, die offen untereinander im Rumpf rollen. */
export type DetailsBodyId = 'start' | 'deadline' | 'duration' | 'recurrence'

/** Die Werte, die im Kopf als Chip stehen und als Popover aufgehen. */
export type DetailsChipId = 'attention' | 'tags' | 'reward' | 'reminders'

/**
 * **Wo der Eintrag steht** (entschieden 2026-09-19).
 *
 * `head` — im Kopf, ohne Rollen erreichbar: Titel, Notiz, Unteraufgaben und die
 * drei Chips. `body` — ein Bereich, der offen untereinander steht und gerollt
 * wird. Bis dahin standen alle zehn als gleichrangige Bereiche im Rumpf; die
 * drei Einordnungs-Werte kosteten als offene Bereiche die Hälfte der Rolllänge.
 */
export type DetailsSectionPlace = 'head' | 'body'

/**
 * In welcher **Pille** der Zeichen-Leiste das Zeichen steht — 1 „was die Aufgabe
 * ausmacht", 2 „wann sie dran ist". `null` heißt: in keiner, der Eintrag trägt
 * kein Zeichen.
 */
export type DetailsSectionGroup = 1 | 2 | null

export interface DetailsSection {
  id: DetailsSectionId
  /** Übersetzungsschlüssel der Überschrift. */
  labelKey: string
  /** Trägt die Aufgabe hier einen Wert? Setzt den Punkt am Zeichen in der Leiste. */
  filled: boolean
  /** Kopf oder Rumpf — siehe {@link DetailsSectionPlace}. */
  place: DetailsSectionPlace
  /**
   * Die Pille, in der das Zeichen steht.
   *
   * **Die Gruppierung steht HIER und nicht in der Leiste**: Sie ist dieselbe
   * Folge, die auch die Einträge untereinander haben — stünde sie an beiden
   * Orten, gäbe es zwei Wahrheiten über eine Reihenfolge.
   */
  group: DetailsSectionGroup
}

interface SectionDefinition {
  id: DetailsSectionId
  labelKey: string
  /** Nur bei genau EINER ausgewählten Aufgabe sichtbar (Titel, Notiz, Unteraufgaben). */
  single?: true
  place: DetailsSectionPlace
  group: DetailsSectionGroup
  filled: (task: Task) => boolean
}

/** Die Pillen der Leiste, in ihrer Reihenfolge von oben nach unten. */
export const DETAILS_GROUPS = [1, 2] as const

const ORDER: SectionDefinition[] = [
  // ── Kopf: was die Aufgabe IST ──
  // Der Titel trägt seit 2026-09-19 ein Zeichen (den Stift). Vorher hatte er
  // keines, weil er ohnehin ganz oben steht; als GRIFF, der den Caret
  // holt, verdient er eins — und der Entwurf zeichnet ihn so.
  { id: 'title', labelKey: 'taskDetails.section.title', single: true, place: 'head', group: 1, filled: () => true },
  // Die Notiz trägt als einzige kein Zeichen: Sie steht direkt unter dem Titel,
  // ein Sprung dorthin wäre ein Sprung auf etwas, das ohnehin im Bild ist.
  { id: 'notes', labelKey: 'taskDetails.section.notes', single: true, place: 'head', group: null, filled: t => !!t.description },
  // Unteraufgaben zeigen ihre eigene Zahl — ein Punkt am Zeichen wäre eine
  // zweite, schlechtere Aussage über denselben Wert.
  { id: 'subtasks', labelKey: 'taskDetails.section.subtasks', single: true, place: 'head', group: 1, filled: () => false },
  { id: 'attention', labelKey: 'taskDetails.section.attention', place: 'head', group: 1, filled: t => t.attention != null },
  { id: 'tags', labelKey: 'taskDetails.section.tags', place: 'head', group: 1, filled: t => t.tags.length > 0 },
  { id: 'reward', labelKey: 'taskDetails.section.reward', place: 'head', group: 1, filled: t => t.rewardId != null },
  // **Erinnerungen ohne Zeichen in der Leiste** (Plan task-reminders, Entscheidung 12): Die Pillen
  // bleiben bei ihrer Länge. Nur bei EINER Aufgabe — Erinnerungen hängen an ihr, nicht an einer Auswahl.
  { id: 'reminders', labelKey: 'taskDetails.section.reminders', single: true, place: 'head', group: null, filled: () => false },

  // ── Rumpf: wann sie dran ist ──
  { id: 'start', labelKey: 'taskDetails.section.start', place: 'body', group: 2, filled: t => t.startDate != null },
  // **Deadline vor Dauer**: Beide beantworten „wann", aber die Deadline steht
  // näher am Start — erst der Rahmen (ab wann, bis wann), dann der Aufwand darin.
  { id: 'deadline', labelKey: 'taskDetails.section.deadline', place: 'body', group: 2, filled: t => t.dueDate != null },
  { id: 'duration', labelKey: 'taskDetails.section.duration', place: 'body', group: 2, filled: t => t.estimatedDuration != null },
  { id: 'recurrence', labelKey: 'taskDetails.section.recurrence', place: 'body', group: 2, filled: t => t.recurrence != null },
]

/**
 * Was die **Aufgaben-Details** zeigen — in der Reihenfolge aus dem Konzept: erst
 * was die Aufgabe IST (Kopf), dann wann sie dran ist (Rumpf).
 *
 * Bei MEHREREN ausgewählten Aufgaben fallen Titel, Notiz und Unteraufgaben weg:
 * Drei Titel passen nicht in ein Feld (CONTEXT.md, Aufgaben-Details). Die drei
 * Chips bleiben — sie gelten der ganzen Auswahl wie jede andere Eigenschaft.
 * `filled` folgt dann derselben Regel wie die Haken im **Aufgaben-Menü**:
 * gesetzt heißt, ALLE tragen es.
 *
 * `checklist: false` — die Aufgabe trägt keine Unteraufgaben: eine Unteraufgabe auf Ebene 4 (#181;
 * bis dahin trug keine Unteraufgabe welche). Belohnung und Wiederholung trägt sie seit #205 — die
 * Wiederholung gesperrt unter einem wiederkehrenden Vorfahren (`recurrenceLocked`).
 */
export function useDetailsSections(tasks: readonly Task[], { checklist = true }: { checklist?: boolean } = {}): DetailsSection[] {
  return useMemo(() => {
    if (tasks.length === 0) return []
    const single = tasks.length === 1
    return ORDER
      .filter(entry => single || !entry.single)
      .filter(entry => checklist || entry.id !== 'subtasks')
      .map(entry => ({
        id: entry.id,
        labelKey: entry.labelKey,
        place: entry.place,
        group: entry.group,
        // Dieselbe Regel wie `taskMenuMarks`: gesetzt heißt, ALLE tragen es.
        filled: tasks.every(entry.filled),
      }))
  }, [tasks, checklist])
}
